// src/components/ui/forms/Input/Input.jsx
import React, { forwardRef } from 'react';
import { cn } from '../../../../utils/cn';

const Input = forwardRef(({
  type = 'text',
  placeholder = '',
  value,
  onChange,
  onFocus,
  onBlur,
  disabled = false,
  required = false,
  error = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  rightElement,
  className = '',
  inputClassName = '',
  size = 'md',
  variant = 'default',
  ...props
}, ref) => {
  // Container styles
  const containerStyles = cn(
    "relative",
    className
  );

  // Size configurations
  const sizeConfig = {
    sm: {
      input: "py-2 text-sm",
      leftPadding: "pl-10",
      rightPadding: "pr-10",
      icon: "w-4 h-4"
    },
    md: {
      input: "py-3 text-base",
      leftPadding: "pl-12",
      rightPadding: "pr-12",
      icon: "w-5 h-5"
    },
    lg: {
      input: "py-4 text-lg",
      leftPadding: "pl-14",
      rightPadding: "pr-14",
      icon: "w-6 h-6"
    }
  };

  // Variant styles
  const variantStyles = {
    default: cn(
      "bg-slate-800/50 border border-slate-600/50",
      "text-slate-200 placeholder-slate-400",
      "focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50",
      error && "border-red-500/50 focus:ring-red-500/50 focus:border-red-500/50"
    ),
    
    // For partner code input (centered, monospace)
    code: cn(
      "bg-slate-800/50 border border-slate-600/50",
      "text-slate-200 placeholder-slate-400",
      "focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50",
      "text-center font-mono tracking-wider",
      error && "border-red-500/50 focus:ring-red-500/50 focus:border-red-500/50"
    )
  };

  const currentSize = sizeConfig[size] || sizeConfig.md;

  // Input base styles
  const inputStyles = cn(
    "w-full rounded-xl transition-all duration-200",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    currentSize.input,
    LeftIcon && currentSize.leftPadding,
    (RightIcon || rightElement) && currentSize.rightPadding,
    !LeftIcon && !RightIcon && !rightElement && "px-4",
    variantStyles[variant],
    inputClassName
  );

  // Icon styles
  const iconStyles = cn(
    "absolute inset-y-0 flex items-center pointer-events-none text-slate-400",
    currentSize.icon
  );

  return (
    <div className={containerStyles}>
      {/* Left Icon */}
      {LeftIcon && (
        <div className={cn("absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400")}>
          <LeftIcon className={currentSize.icon} />
        </div>
      )}

      {/* Input Field */}
      <input
        ref={ref}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={inputStyles}
        {...props}
      />

      {/* Right Icon or Element */}
      {RightIcon && !rightElement && (
        <div className={cn("absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-400")}>
          <RightIcon className={currentSize.icon} />
        </div>
      )}

      {/* Right Element (for interactive elements like password toggle) */}
      {rightElement && (
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
          {rightElement}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;